import React, { useEffect, useState } from 'react';
import { Modal } from './index';
import { CheckCircle2, Ticket, PartyPopper } from 'lucide-react';

const formatDeadline = (date) => {
  if (!date) return null;
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const buildContent = ({ mode, ticketCount, paidCount, deadlineDate }) => {
  const deadlineStr = formatDeadline(deadlineDate);
  const untilClause = deadlineStr ? `hasta el ${deadlineStr}` : 'un plazo limitado';
  const plural = ticketCount === 1 ? '' : 's';

  if (paidCount > 0) {
    const entradasClause = mode === 'existing' ? 'Estas son tus entradas actualmente' : 'Estas son tus nuevas entradas';
    return {
      title: 'Tus entradas están listas',
      message: `${entradasClause}, tenés ${untilClause} para retirarlas. Te recordamos que ya tenés ${paidCount} entrada${paidCount === 1 ? '' : 's'} pagada${paidCount === 1 ? '' : 's'}.`,
      icon: Ticket,
    };
  }

  if (mode === 'existing') {
    const demonstrative = ticketCount === 1 ? 'esta entrada' : 'estas entradas';
    return {
      title: 'Ya tenés una reserva',
      message: `Ya tenés reservadas ${demonstrative}, recordá que tenés ${untilClause} para retirarla${plural}.`,
      icon: Ticket,
    };
  }

  if (mode === 'modified') {
    return {
      title: 'Selección modificada',
      message: `Modificaste tu selección de entradas, tus nuevas entradas son ${ticketCount}, tenés tiempo ${untilClause} para retirarlas.`,
      icon: CheckCircle2,
    };
  }

  if (mode === 'cancelled') {
    if (ticketCount > 0) {
      return {
        title: 'Selección actualizada',
        message: `Cancelaste parte de tu selección. Ahora tenés ${ticketCount} entrada${plural} reservada${plural}, con tiempo ${untilClause} para retirarla${plural}.`,
        icon: CheckCircle2,
      };
    }
    return {
      title: 'Reserva cancelada',
      message: 'Cancelaste tu selección de entradas. Ya no tenés entradas pendientes de retiro para este evento.',
      icon: CheckCircle2,
    };
  }

  return {
    title: '¡Reserva confirmada!',
    message: `Reservaste ${ticketCount} entrada${plural}, tenés tiempo ${untilClause} para retirarla${plural}.`,
    icon: PartyPopper,
  };
};

const ReservationConfirmationModal = ({
  isOpen,
  onClose,
  mode = 'created', // 'created' | 'modified' | 'cancelled' | 'existing'
  ticketCount = 0,
  paidCount = 0,
  deadlineDate = null,
  seatLabels = [],
}) => {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEntered(false);
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
    setEntered(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const { title, message, icon: Icon } = buildContent({ mode, ticketCount, paidCount, deadlineDate });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="sm" showCloseButton={false}>
      <div
        className={`text-center transition-all duration-300 ease-out ${
          entered ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        <div className="flex justify-center mb-5">
          <div className="relative">
            <div className="absolute inset-0 bg-emerald-400 rounded-full blur-xl opacity-30" />
            <div className="relative p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-full">
              <Icon className="w-9 h-9 text-emerald-600" strokeWidth={1.75} />
            </div>
          </div>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>

        <p className="text-gray-600 leading-relaxed mb-5 text-[15px]">{message}</p>

        {seatLabels.length > 0 && (
          <div className="mb-6 text-left rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Entradas seleccionadas
            </p>
            <ul className="space-y-1.5">
              {[...seatLabels]
                .map(item => (typeof item === 'string' ? { label: item, isPaid: false } : item))
                .sort((a, b) => Number(a.isPaid) - Number(b.isPaid))
                .map((item, idx) => (
                  <li key={`${item.label}-${idx}`} className="flex items-center justify-between gap-2 text-sm text-gray-800">
                    <span className="flex items-center gap-2">
                      <Ticket className={`w-3.5 h-3.5 shrink-0 ${item.isPaid ? 'text-green-600' : 'text-blue-600'}`} />
                      <span>
                        {item.label}
                        {item.isPalco && (
                          <span className="text-xs text-gray-500">
                            {' '}({item.palcoCapacity || 4} ubicaciones)
                          </span>
                        )}
                      </span>
                    </span>
                    {item.isPaid ? (
                      <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 rounded-full px-2 py-0.5 shrink-0">
                        <CheckCircle2 className="w-3 h-3" />
                        Pagada
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-gray-500 bg-gray-200 rounded-full px-2 py-0.5 shrink-0">
                        Pendiente de pago
                      </span>
                    )}
                  </li>
                ))}
            </ul>
          </div>
        )}

        <button
          onClick={onClose}
          className="w-full sm:w-auto px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
        >
          Entendido
        </button>
      </div>
    </Modal>
  );
};

export default ReservationConfirmationModal;
